import type { WorkItem } from '../api/workItems';
import { BRAND_NAME } from '../brand';
import { isSuspiciousWorkItemDisplay, normalizeClassificationSource } from './classificationSuspicion';
import { getWorkItemSourceInfo } from './workItemDisplay';

const BEST_GUESS_WHY: Record<string, string> = {
    unknown_source: `${BRAND_NAME} could not tell how this was sorted, so this is a guess.`,
    invalid_negative_confidence: 'The signal was not usable, so this is a guess.',
    legacy_int_confidence_non_positive: 'The signal was not usable, so this is a guess.',
    confidence_untrusted: 'The signals were too thin to be sure, so this is a guess.',
    confidence_below_specific_domain_floor: 'The signals were too thin to be sure, so this is a guess.',
    weak_evidence: 'There was not enough to go on, so this is a guess.',
    non_english_weak_signal: 'This was hard to read confidently, so this is a guess.',
    general_best_guess: 'Nothing pointed to a clearer life area, so this is a guess.',
};

const DEFAULT_WHY = 'The signals were thin, so this is a guess you can change.';

export function classificationQuality(item: WorkItem): Record<string, unknown> | null {
    const raw = item.meta?.classification_quality;
    if (!raw || typeof raw !== 'object') return null;
    return raw as Record<string, unknown>;
}

/** New-ingest placement kept as a life-area guess instead of an Uncertain pile or a fake General. */
export function isBestGuessPlacement(item: WorkItem): boolean {
    return classificationQuality(item)?.best_guess === true;
}

export function bestGuessWhy(item: WorkItem): string {
    const reason = classificationQuality(item)?.best_guess_reason;
    if (typeof reason !== 'string' || !reason.trim()) return DEFAULT_WHY;
    return BEST_GUESS_WHY[reason.trim()] || DEFAULT_WHY;
}

function isManualNeutral(item: WorkItem): boolean {
    return getWorkItemSourceInfo(item).sourceType === 'manual'
        && normalizeClassificationSource(item.classification_source) === 'unknown';
}

/** Dead-end Uncertain badge. Best-guess rows show the life area instead. */
export function showUncertainClassificationCue(item: WorkItem): boolean {
    if (isBestGuessPlacement(item) || isManualNeutral(item)) return false;
    return isSuspiciousWorkItemDisplay(item);
}
